import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext.jsx";
import PrivateRoute from "./context/privateRoute.jsx";
import { useAuth } from "./context/useAuth.jsx";
import { SSEProvider } from "./context/SSEContext.jsx";
import { ActiveWorkflowsProvider } from "./context/ActiveWorkflowsContext.jsx";
import ToastHost from "./components/ToastHost.jsx";
import EmailSignIn from "./components/auth/EmailSignIn.jsx";
import CheckInbox from "./components/auth/CheckInbox.jsx";
import ConfirmSignIn from "./components/auth/ConfirmSignIn.jsx";
import LinkExpired from "./components/auth/LinkExpired.jsx";
import Welcome from "./components/auth/Welcome.jsx";
import { StepUpHost } from "./components/auth/StepUpModal.jsx";
import { PricingPage } from "./components/PricingPage.jsx";
import { TermsPage } from "./components/TermsPage.jsx";
import { PrivacyPage } from "./components/PrivacyPage.jsx";
import { LandingPage } from "./components/LandingPage.jsx";
import { HelpPage } from "./components/HelpPage.jsx";
import { UserProfilePage } from "./components/UserProfilePage.jsx";
import AppLayout from "./components/AppLayout.jsx";
import FirstRunWork from "./features/home/FirstRunWork.jsx";
import FirstRunWays from "./features/home/FirstRunWays.jsx";
import FeedbackWidget from "./components/FeedbackWidget.jsx";
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import { generate as randomWords } from "random-words"; // Import the random-words library
import TextNode from "./components/nodes/TextNode";
import ImageNode from "./components/nodes/ImageNode";
import GoogleSheetsNode from "./components/nodes/GoogleSheetsNode";
import PromptNode from "./components/nodes/PromptNode"; // Import the new PromptNode
import MapNode from "./components/nodes/MapNode";
import ContextMenu from "./components/ContextMenu";
import CanvasChatBar from "./components/CanvasChatBar";
import ToolPanel from "./components/ToolPanel";
import ToolNode from "./components/nodes/ToolNode";
import ToolOutputNode from "./components/nodes/ToolOutputNode";
import FileNode from "./components/nodes/FileNode";
import ActionModal from "./components/ActionModal.jsx";
import Toolbar from "./components/Toolbar";
import TaskSelectionModal from "./components/TaskSelectionModal.jsx";
import FilePicker from "./components/FilePicker.jsx";
import FlowPickerModal from "./components/FlowPickerModal.jsx";
import { saveGraph } from "./utils/indexedDB";
import { generateUniqueId, extractContentFromResponse, predictOutputModality, extractFileSchema } from "./utils/helpers";
import { getOperationColor } from "./utils/operationColors";
import { mockApiCall } from "./api/mockApi";
import { fetchCanvasSuggestions } from "./api/canvasSuggestApi";
import { saveFlow, listFlows, getFlow } from "./api/flowsApi";
import { fetchFileText } from "./api/filesApi";
import { fetchGoogleSheetData } from "./api/googleSheetsApi";
import {
  summarizeText,
  shortFormText,
  expandText,
  classifyPayload,
  sentimentPayload,
  generateImageFromText,
} from "./api/textApi";
import {
  describeImage,
  tagImage,
  classifyImage,
  sentimentImage,
} from "./api/imageApi";

import { getStaticMapUrl } from "./api/googleMapsApi";
import { smartUpload } from "./api/filesApi";
import MainApp, { modeRouteElements } from './components/MainApp';
import HomePage from './features/home/HomePage';
import { TasksRoute } from './features/tasks/TasksPage';
import RunTaskPage from './features/tasks/RunTaskPage';
import SavedTaskPage from './features/tasks/SavedTaskPage';
import PublicTaskPage from './features/tasks/PublicTaskPage';
import { RunsRoute } from './features/runs/RunsPage';
import { DataRoute } from './features/data/DataPage';
import BillingPage from './components/BillingPage';
import BillingSuccess from './components/BillingSuccess';
import BillingCancel from './components/BillingCancel';
import BillingDashboard from './components/billing/BillingDashboard';
import OrgBillingPage from './components/OrgBillingPage';
import useTreeLayout from "./hooks/useHybridLayout";
import { useCanvasDeepLink } from "./hooks/useCanvasDeepLink";

// Define node types
const nodeTypes = {
  textNode: TextNode,
  imageNode: ImageNode,
  promptNode: PromptNode,
  googleSheetsNode: GoogleSheetsNode,
  mapNode: MapNode,
  toolNode: ToolNode,
  toolOutputNode: ToolOutputNode,
  fileNode: FileNode,
};

// Initial nodes and edges — canvas starts empty (just the hidden root anchor)
const initialNodes = [
  {
    id: "root",
    type: "textNode",
    position: { x: 0, y: 0 },
    data: { title: "Root", content: "", outputs: [], classificationLocks: {} },
    hidden: true,
  },
];

const initialEdges = [];

// Calculate position for a new node based on parent and index
const calculateNodePosition = (parentNode, index = 0) => {
  const angle = (index * Math.PI) / 4 + Math.PI / 2; // Distribute nodes in a semi-circle below parent
  const distance = 200; // Distance from parent

  return {
    x: parentNode.position.x + Math.cos(angle) * distance,
    y: parentNode.position.y + Math.sin(angle) * distance + 50,
  };
};

// Canvas view component (formerly GraphApp)
export function CanvasView({ onQuickWorkflow }) {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [graphName, setGraphName] = useState(
    randomWords({ exactly: 3, join: "-" }),
  ); // Generate random name
  const [graphId, setGraphId] = useState(null);
  const [flowId, setFlowId] = useState(null); // server-side flow UUID
  const autoSaveTimerRef = useRef(null);
  const [showFlowPicker, setShowFlowPicker] = useState(false);

  // Center the initial graph in the viewport once the React Flow instance is ready
  useEffect(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ padding: 0.2, includeHiddenNodes: true });
    }
  }, [reactFlowInstance]);

  const [autoClassify, setAutoClassify] = useState(false);
  const [autoSentiment, setAutoSentiment] = useState(false);
  const [taskDescription, setTaskDescription] = useState("");
  const [activeRole, setActiveRole] = useState("");
  const [activeRoleLabel, setActiveRoleLabel] = useState("");

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const [toolbarOpen, setToolbarOpen] = useState(!isMobile);

  // text menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showTaskSelectionModal, setShowTaskSelectionModal] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [filePickerMode, setFilePickerMode] = useState(null); // 'attach' or 'select'

  // Track child count for each node to distribute children
  const [nodeChildCount, setNodeChildCount] = useState({});

  // Tool panel
  const [toolPanelOpen, setToolPanelOpen] = useState(true);

  // Pre-fill text for chat bar (set when user clicks a prompt node to edit it)
  const [chatPrefill, setChatPrefill] = useState('');

  // Canvas suggestion cache: nodeId → { suggestions, fetchedAt }
  const [suggestionCache, setSuggestionCache] = useState({});
  const SUGGESTION_TTL_MS = 300_000; // 5 min

  // Mark nodes as read-only when they have children
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: { ...node.data, readOnly: (nodeChildCount[node.id] || 0) > 0 },
      })),
    );
  }, [nodeChildCount, setNodes]);

  // Handle a node's data change
  const handleNodeChange = useCallback(
    (nodeId, updatedData) => {
      setNodes((prevNodes) =>
        prevNodes.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...updatedData } }
            : node,
        ),
      );
    },
    [setNodes],
  );

  // Update a prompt node's content
  const handlePromptChange = useCallback(
    (nodeId, updatedData) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...updatedData } }
            : node,
        ),
      );
    },
    [setNodes, taskDescription],
  );

  const getDescendants = useCallback(
    (id) => {
      const result = [];
      const stack = [id];
      while (stack.length) {
        const current = stack.pop();
        edges.forEach((e) => {
          if (e.source === current) {
            result.push(e.target);
            stack.push(e.target);
          }
        });
      }
      return result;
    },
    [edges],
  );

  const getParentNodes = useCallback(
    (id, count = 3) => {
      const parents = [];
      let currentId = id;
      for (let i = 0; i < count; i++) {
        const parentEdge = edges.find((e) => e.target === currentId);
        if (!parentEdge) break;
        const parentNode = nodes.find((n) => n.id === parentEdge.source);
        if (!parentNode) break;
        parents.push({
          id: parentNode.id,
          type: parentNode.type,
          content:
            parentNode.type === "imageNode"
              ? parentNode.data.imageUrl
              : parentNode.data.content,
        });
        currentId = parentNode.id;
      }
      return parents;
    },
    [edges, nodes],
  );

  // Pre-fetch suggestions for a node immediately after it finishes executing.
  // Caches result by nodeId; stale entries are ignored after SUGGESTION_TTL_MS.
  const prefetchSuggestions = useCallback(
    (node, parentNodes) => {
      if (!node || node.data?.isInput) return;
      const nodeId = node.id;
      const cached = suggestionCache[nodeId];
      if (cached && Date.now() - cached.fetchedAt < SUGGESTION_TTL_MS) return;

      const pathHistory = (parentNodes || []).map((p) => ({
        type: p.type === 'imageNode' ? 'image' : 'text',
        operation: null,
        summary: typeof p.content === 'string' ? p.content.slice(0, 80) : null,
      }));

      const isFile = node.type === 'fileNode';
      const fileContext = isFile ? {
        filename: node.data?.filename || null,
        mime_type: node.data?.mime_type || null,
        schema_preview: node.data?.schema_preview || null,
        available_tools: ['summarize', 'expand', 'classify', 'extract-key-messages', 'bullet-points'],
      } : undefined;

      fetchCanvasSuggestions({
        nodeId,
        nodeType: isFile ? 'fileNode' : (node.type === 'imageNode' ? 'image' : 'text'),
        nodeContent: isFile
          ? (node.data?.schema_preview || node.data?.filename || '')
          : (node.type === 'imageNode' ? (node.data?.imageUrl || '') : (node.data?.content || '')),
        pathHistory,
        role: activeRole || undefined,
        fileContext,
      })
        .then((result) => {
          setSuggestionCache((prev) => ({
            ...prev,
            [nodeId]: { suggestions: result.suggestions || [], fetchedAt: Date.now() },
          }));
        })
        .catch(() => {}); // silent — suggestions are best-effort
    },
    [suggestionCache, activeRole, SUGGESTION_TTL_MS],
  );

  const collapseNode = useCallback(
    (id) => {
      const descendants = getDescendants(id);
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === id)
            return { ...n, data: { ...n.data, collapsed: true } };
          if (descendants.includes(n.id)) return { ...n, hidden: true };
          return n;
        }),
      );
      setEdges((eds) =>
        eds.map((e) =>
          descendants.includes(e.source) || descendants.includes(e.target)
            ? { ...e, hidden: true }
            : e,
        ),
      );
    },
    [setNodes, setEdges, getDescendants],
  );

  const expandNode = useCallback(
    (id) => {
      const descendants = getDescendants(id);
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === id)
            return { ...n, data: { ...n.data, collapsed: false } };
          if (descendants.includes(n.id)) return { ...n, hidden: false };
          return n;
        }),
      );
      setEdges((eds) =>
        eds.map((e) =>
          descendants.includes(e.source) || descendants.includes(e.target)
            ? { ...e, hidden: false }
            : e,
        ),
      );
    },
    [setNodes, setEdges, getDescendants],
  );

  const deleteNode = useCallback(
    (id) => {
      const descendants = getDescendants(id);
      setNodes((nds) =>
        nds.filter((n) => n.id !== id && !descendants.includes(n.id)),
      );
      setEdges((eds) => {
        const newEdges = eds.filter(
          (e) =>
            e.source !== id &&
            e.target !== id &&
            !descendants.includes(e.source) &&
            !descendants.includes(e.target),
        );
        const childCountMap = {};
        newEdges.forEach((e) => {
          childCountMap[e.source] = (childCountMap[e.source] || 0) + 1;
        });
        setNodeChildCount(childCountMap);
        return newEdges;
      });
    },
    [setNodes, setEdges, getDescendants],
  );

  const createCallbacks = useCallback(
    (id, type) => ({
      onChange:
        type === "promptNode"
          ? (updated) => handlePromptChange(id, updated)
          : (updated) => handleNodeChange(id, updated),
      onCollapse: () => collapseNode(id),
      onExpand: () => expandNode(id),
      onDelete: () => deleteNode(id),
    }),
    [
      handlePromptChange,
      handleNodeChange,
      collapseNode,
      expandNode,
      deleteNode,
    ],
  );

  // attach callbacks to initial nodes
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          ...createCallbacks(node.id, node.type),
        },
      })),
    );
  }, [createCallbacks, setNodes]);

  // Whenever we are adding tags (sentiment/classification)
  const applyTagToNode = useCallback(
    async (node, operation) => {
      try {
        const parentNodes = getParentNodes(node.id);
        let response;
        // We will have standard responses, sucha s classify and sentiment
        if (node.type === "imageNode") {
          if (operation === "classify") {
            response = await classifyImage({
              imageUrl: node.data.imageUrl,
              role: activeRole,
              task: taskDescription,
              context: parentNodes,
            });
          } else {
            response = await sentimentImage({
              imageUrl: node.data.imageUrl,
              role: activeRole,
              task: taskDescription,
              context: parentNodes,
            });
          }
        } else {
          const textContent = (node.data.content || "").trim();
          if (operation === "classify") {
            response = await classifyPayload({
              content: textContent,
              role: activeRole,
              task: taskDescription,
              context: parentNodes,
            });
          } else {
            response = await sentimentPayload({
              content: textContent,
              role: activeRole,
              task: taskDescription,
              context: parentNodes,
            });
          }
        }
        let label;
        if (operation === "classify") {
          label =
            response.label ||
            response.classification ||
            (response.content && response.content.split(":").pop().trim());
        } else {
          label =
            response.label ||
            response.sentiment ||
            (response.content && response.content.split(":").pop().trim());
        }
        const tagType = operation === "classify" ? "types" : "sentiment";
        const currentContent =
          node.type === "imageNode" ? node.data.imageUrl : node.data.content;

        setNodes((nds) =>
          nds.map((n) => {
            if (n.id !== node.id) return n;

            const existingTags = n.data.tags || [];
            const tagIndex = existingTags.findIndex((t) => t.type === tagType);
            const classificationLocks = n.data.classificationLocks || {};

            if (
              tagIndex !== -1 &&
              classificationLocks[tagType] === currentContent
            ) {
              return n;
            }

            if (!label) {
              return n;
            }

            const newTags = [...existingTags];

            if (tagIndex !== -1) {
              newTags[tagIndex] = { type: tagType, value: label };
            } else {
              newTags.push({ type: tagType, value: label });
            }

            return {
              ...n,
              data: {
                ...n.data,
                tags: newTags,
                classificationLocks: {
                  ...classificationLocks,
                  [tagType]: currentContent,
                },
              },
            };
          }),
        );
      } catch (e) {
        console.error("Auto tagging failed", e);
      }
    },
    [setNodes, taskDescription, getParentNodes, activeRole],
  );

  // Force layout integration
  const handlePositionChange = useCallback(
    (updatedPositions) => {
      setNodes((nds) => {
        let changed = false;
        const newNodes = nds.map((node) => {
          const position = updatedPositions.find((pos) => pos.id === node.id);
          if (position) {
            // Compare positions
            if (
              node.position.x !== position.position.x ||
              node.position.y !== position.position.y
            ) {
              changed = true;
              return {
                ...node,
                position: position.position,
              };
            }
          }
          return node;
        });
        // Only update if something changed
        return changed ? newNodes : nds;
      });
    },
    [setNodes],
  );

  // Tree layout — triggered imperatively after node/edge changes, not reactively.
  // This avoids the render loop that occurs when setNodes() inside a useEffect
  // re-fires the effect on every render.
  const { applyTreeLayout } = useTreeLayout(handlePositionChange, {
    treeHorizontalSpacing: 280,
    treeVerticalSpacing: 30, // gap between bottom of parent and top of child
  });

  // Handle connections between nodes
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  // Save the ReactFlow instance when it's ready
  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
  }, []);

  // Handle node right-click to show context menu
  const onNodeContextMenu = useCallback((event, node) => {
    // Prevent default context menu
    event.preventDefault();

    if (node?.data?.collapsed) return;

    // Set the selected node and context menu position
    setSelectedNode(node);
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeType: node.type,
      nodeData: {
        ...node.data,
        additionalInstructions: node.data.additionalInstructions || "",
      },
    });

    // Trigger suggestion prefetch for file nodes (not done post-execution like output nodes)
    if (node.type === 'fileNode') {
      prefetchSuggestions(
        { id: node.id, type: node.type, data: { content: node.data.schema_preview || node.data.filename || '' } },
        []
      );
    }
  }, [prefetchSuggestions]);

  // Node click: select node for chat bar (all devices); also show context menu on touch.
  // Clicking a prompt node focuses the chat bar on its parent with the prompt text pre-filled.
  const onNodeClick = useCallback((event, node) => {
    if (node?.data?.collapsed || node?.id === 'root') return;

    if (node.data?.isPrompt) {
      const parentEdge = edges.find((e) => e.target === node.id);
      const parentNode = parentEdge ? nodes.find((n) => n.id === parentEdge.source) : null;
      if (parentNode && parentNode.id !== 'root') {
        setSelectedNode(parentNode);
        setChatPrefill(node.data.content || '');
        return;
      }
    }

    setSelectedNode(node);

    const ptr = event.pointerType || event.nativeEvent?.pointerType;
    if (ptr === "touch") {
      event.preventDefault();
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        nodeType: node.type,
        nodeData: { ...node.data },
      });
    }
  }, [edges, nodes]);

  const onPaneContextMenu = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowWrapper.current || !reactFlowInstance) return;
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const flowPosition = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });
      const rootNode = nodes.find((n) => n.id === "root");
      if (!rootNode) return;
      setSelectedNode(rootNode);
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        nodeType: "root",
        nodeData: rootNode.data,
        flowPosition,
      });
    },
    [nodes, reactFlowInstance],
  );

  // Handle pane clicks for mobile context menu and closing
  const onPaneClick = useCallback(
    (event) => {
      const ptr = event.pointerType || event.nativeEvent?.pointerType;
      if (ptr === "touch") {
        event.preventDefault();
        if (contextMenu) {
          setContextMenu(null);
          return;
        }
        if (!reactFlowWrapper.current || !reactFlowInstance) return;
        const bounds = reactFlowWrapper.current.getBoundingClientRect();
        const flowPosition = reactFlowInstance.project({
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
        });
        const rootNode = nodes.find((n) => n.id === "root");
        if (!rootNode) return;
        setSelectedNode(rootNode);
        setContextMenu({
          x: event.clientX,
          y: event.clientY,
          nodeType: "root",
          nodeData: rootNode.data,
          flowPosition,
        });
      } else {
        // Desktop pane click: deselect node (clears chat bar target)
        setSelectedNode(null);
        setContextMenu(null);
      }
    },
    [contextMenu, nodes, reactFlowInstance],
  );

  const handleOpenActionModal = useCallback(() => {
    setContextMenu(null);
    setShowActionModal(true);
  }, []);

  const handleAnalyzeWorkflow = useCallback((content) => {
    setContextMenu(null);
    if (onQuickWorkflow && content) {
      // Format content for workflow analysis
      const description = `Analyze and process: ${content.substring(0, 200)}${content.length > 200 ? '...' : ''}`;
      onQuickWorkflow(description);
    }
  }, [onQuickWorkflow]);

  // Handle upload and attach file to node
  const handleUploadAttachFile = useCallback(() => {
    setContextMenu(null);
    // Create file input for upload
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (file) {
        try {
          // Upload file to fileserver
          console.log('Uploading file:', file.name);
          const uploadResult = await smartUpload(file, (percent) => {
            console.log(`Upload progress: ${percent}%`);
          });

          console.log('File uploaded:', uploadResult);

          // Attach to node
          if (selectedNode) {
            setNodes((nds) =>
              nds.map((n) =>
                n.id === selectedNode.id
                  ? {
                      ...n,
                      data: {
                        ...n.data,
                        attachedFiles: [
                          ...(n.data.attachedFiles || []),
                          {
                            file_id: uploadResult.file_id,
                            filename: uploadResult.filename,
                            file_size: uploadResult.file_size,
                            mime_type: uploadResult.mime_type,
                          }
                        ]
                      }
                    }
                  : n
              )
            );
          }
        } catch (error) {
          console.error('File upload failed:', error);
          alert(`File upload failed: ${error.message}`);
        }
      }
    };
    input.click();
  }, [selectedNode, setNodes]);

  // Handle select from library (show FilePicker)
  const handleSelectFromLibrary = useCallback(() => {
    setContextMenu(null);
    setFilePickerMode('select');
    setShowFilePicker(true);
  }, []);

  // Handle file selection from FilePicker
  const handleFileSelected = useCallback((selectedFiles) => {
    if (!selectedNode) return;

    // selectedFiles can be an array (multiSelect) or single file
    const files = Array.isArray(selectedFiles) ? selectedFiles : [selectedFiles];

    // Attach selected files to node
    setNodes((nds) =>
      nds.map((n) =>
        n.id === selectedNode.id
          ? {
              ...n,
              data: {
                ...n.data,
                attachedFiles: [
                  ...(n.data.attachedFiles || []),
                  ...files.map(f => ({
                    file_id: f.file_id,
                    filename: f.filename,
                    file_size: f.file_size,
                    mime_type: f.mime_type,
                  }))
                ]
              }
            }
          : n
      )
    );
  }, [selectedNode, setNodes]);

  // Create a new node based on context menu selection
  const handleCreateNode = useCallback(
    async (operationData) => {
      try {
        if (!selectedNode) return;

        // Determine if operationData is an object or a string
        const operation =
          typeof operationData === "string"
            ? operationData
            : operationData.operation;
        const label =
          typeof operationData === "object" && operationData.label
            ? operationData.label
            : operation;
        const imageUrl =
          typeof operationData === "object"
            ? operationData.imageUrl || operationData.image_url
            : null;
        const fileContent =
          typeof operationData === "object" ? operationData.content : null;
        const promptText =
          typeof operationData === "object" ? operationData.promptText || null : null;

        // Get the parent node's data
        const parentNode = nodes.find((n) => n.id === selectedNode.id);
        if (!parentNode) return;
        const parentNodes = getParentNodes(parentNode.id);

        // Handle classification and sentiment tagging
        if (operation === "classify" || operation === "sentiment") {
          let response;
          if (parentNode.type === "imageNode") {
            if (operation === "classify") {
              response = await classifyImage({
                imageUrl: parentNode.data.imageUrl,
                role: activeRole,
                task: taskDescription,
                context: parentNodes,
              });
            } else {
              response = await sentimentImage({
                imageUrl: parentNode.data.imageUrl,
                role: activeRole,
                task: taskDescription,
                context: parentNodes,
              });
            }
          } else {
            const textContent = (parentNode.data.content || "").trim();
            if (operation === "classify") {
              response = await classifyPayload({
                content: textContent,
                role: activeRole,
                task: taskDescription,
                context: parentNodes,
              });
            } else {
              response = await sentimentPayload({
                content: textContent,
                role: activeRole,
                task: taskDescription,
                context: parentNodes,
              });
            }
          }
          let label;
          if (operation === "classify") {
            label =
              response.label ||
              response.classification ||
              (response.content && response.content.split(":").pop().trim());
          } else {
            label =
              response.label ||
              response.sentiment ||
              (response.content && response.content.split(":").pop().trim());
          }
          const tagType = operation === "classify" ? "types" : "sentiment";
          const currentContent =
            parentNode.type === "imageNode"
              ? parentNode.data.imageUrl
              : parentNode.data.content;

          setNodes((nds) =>
            nds.map((n) => {
              if (n.id !== parentNode.id) return n;

              const existingTags = n.data.tags || [];
              const tagIndex = existingTags.findIndex(
                (t) => t.type === tagType,
              );
              const classificationLocks = n.data.classificationLocks || {};

              // If already classified for this content, do nothing
              if (
                tagIndex !== -1 &&
                classificationLocks[tagType] === currentContent
              ) {
                return n;
              }

              if (!label) {
                return n;
              }

              const newTags = [...existingTags];

              if (tagIndex !== -1) {
                newTags[tagIndex] = { type: tagType, value: label };
              } else {
                newTags.push({ type: tagType, value: label });
              }

              return {
                ...n,
                data: {
                  ...n.data,
                  tags: newTags,
                  classificationLocks: {
                    ...classificationLocks,
                    [tagType]: currentContent,
                  },
                },
              };
            }),
          );

          setContextMenu(null);
          return;
        }

        // Generate a unique ID for the new node
        const newNodeId = generateUniqueId();

        // Update child count for this parent
        const currentChildCount = nodeChildCount[parentNode.id] || 0;
        setNodeChildCount((prev) => ({
          ...prev,
          [parentNode.id]: currentChildCount + 1,
        }));

        // Calculate position for the new node based on parent and child index
        const position =
          parentNode.id === "root" && contextMenu?.flowPosition
            ? contextMenu.flowPosition
            : calculateNodePosition(parentNode, currentChildCount);

        // Determine node type based on operation
        const isPromptNode = operation === "create-prompt";
        const isImageNode = operation.includes("image");
        const isGoogleSheetsNode = operation === "google-sheets";
        const isMapNode = operation === "create-map";
        const isLoadFile = operation === "load-file";
        const isNewTextNode = operation === "new-text-node" || isLoadFile;
        const isNewImageNode = operation === "new-image-node";

        // We need to make this more generic if we are going to infer from
        // mcp tooling
        const nodeType = isPromptNode
          ? "promptNode"
          : isImageNode
            ? "imageNode"
            : isGoogleSheetsNode
              ? "googleSheetsNode"
              : isMapNode
                ? "mapNode"
                : "textNode";

        const color = getOperationColor(operation);

        const stubNode = {
          id: newNodeId,
          type: nodeType,
          position,
          data: {
            title: isNewTextNode
              ? "New Text Node"
              : isNewImageNode
                ? "New Image Node"
                : isPromptNode
                  ? "Prompt"
                  : "Loading...",
            content: isPromptNode ? (promptText || "") : "",
            imageUrl: "",
            mapUrl: "",
            sheetData: undefined,
            outputs: [],
            isLoading: !(isNewTextNode || isNewImageNode || isPromptNode),
            readOnly: isPromptNode || !(isNewTextNode || isNewImageNode || isPromptNode),
            isInput: isNewTextNode || isNewImageNode,
            isOutput: !(isNewTextNode || isNewImageNode || isPromptNode),
            isPrompt: isPromptNode,
            classificationLocks: {},
            handleColor: color,
            ...createCallbacks(newNodeId, nodeType),
          },
        };

        const newEdge = {
          id: `e${parentNode.id}-${newNodeId}`,
          source: parentNode.id,
          target: newNodeId,
          type: "smoothstep",
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
          label,
          style: { stroke: color },
          labelStyle: { fill: color, fontSize: 10 },
          hidden: parentNode.id === "root",
        };

        // Close the context menu first to prevent ResizeObserver issues
        setContextMenu(null);

        requestAnimationFrame(() => {
          setNodes((nds) => [...nds, stubNode]);
          requestAnimationFrame(() => {
            setEdges((eds) => {
              const nextEdges = [...eds, newEdge];
              // Trigger layout imperatively now that topology has changed.
              // Use setTimeout(0) so setNodes has flushed first.
              setTimeout(() => {
                setNodes((currentNodes) => {
                  applyTreeLayout(currentNodes, nextEdges, {
                    newNodeId,
                    rfInstance: reactFlowInstance,
                  });
                  return currentNodes; // no-op update, just reads latest nodes
                });
              }, 0);
              return nextEdges;
            });
          });
        });

        let response;
        // TODO: refactor so that this is dynamic and possibly passed back from the API
        if (isNewTextNode) {
          console.log("we do new nodes. textnodes.");
          response = { content: isLoadFile ? fileContent || "" : "" };
        } else if (isNewImageNode) {
          response = { content: "", imageUrl: "" };
        } else if (isPromptNode) {
          // Prompt node content is already set in the stub; nothing to execute.
          response = { content: promptText || "" };
        } else if (isMapNode) {
          response = { mapUrl: getStaticMapUrl(parentNode.data.content) };
        } else if (isGoogleSheetsNode) {
          const sheetData = await fetchGoogleSheetData();
          response = { content: "", sheetData };
        } else if (imageUrl) {
          response = { content: "", imageUrl };
        } else {
          const payloadData = {
            content: (parentNode.data.content || "").trim(),
            additional: parentNode.data.additionalInstructions || "",
            parentNodeId: [parentNode.id],
            metadata: {},
            classify: autoClassify ? "true" : "false",
            sentiment: autoSentiment ? "true" : "false",
            role: activeRole,
            task: taskDescription,
            context: parentNodes,
          };
          // we are doing an action
          if (operation === "summary") {
            response = await summarizeText(payloadData);
          } else if (operation === "short-form") {
            response = await shortFormText(payloadData);
          } else if (operation === "expand") {
            response = await expandText(payloadData);
          } else if (operation === "generate-image") {
            response = await generateImageFromText(payloadData);
          } else if (parentNode.type === "imageNode") {
            const imageData = {
              imageUrl: parentNode.data.imageUrl,
              role: activeRole,
              task: taskDescription,
              context: parentNodes,
            };
            if (operation === "describe") {
              response = await describeImage(imageData);
            } else if (operation === "tag") {
              response = await tagImage(imageData);
            } else if (operation === "classify") {
              response = await classifyImage(imageData);
            } else if (operation === "sentiment") {
              response = await sentimentImage(imageData);
            } else {
              response = await mockApiCall({
                parentNodeId: parentNode.id,
                parentNodeContent: parentNode.data.content,
                parentNodeType: parentNode.type,
                operation: operation,
                role: activeRole,
                task: taskDescription,
                context: parentNodes,
              });
            }
          } else if (operation === "classify") {
            response = await classifyPayload(payloadData);
          } else if (operation === "sentiment") {
            response = await sentimentPayload(payloadData);
          } else {
            response = await mockApiCall({
              parentNodeId: parentNode.id,
              parentNodeContent: parentNode.data.content,
              parentNodeType: parentNode.type,
              operation: operation,
              role: activeRole,
              task: taskDescription,
              context: parentNodes,
            });
          }
        }

        const updatedData = {
          title: isPromptNode
            ? (promptText && promptText.length <= 40 ? promptText : "Prompt")
            : isMapNode
              ? `Map of ${parentNode.data.title}`
              : isNewImageNode
                ? "New Image Node"
                : isNewTextNode
                  ? "New Text Node"
                  : `Response From ${parentNode.data.title}`,
          content: extractContentFromResponse(operation, response),
          sheetData: response.sheetData,
          outputs: isPromptNode || isMapNode ? [] : ["Out 1", "Out 2", "Out 3"],
          imageUrl: response.imageUrl || response.image_url,
          mapUrl: response.mapUrl,
          ...(isPromptNode
            ? { onChange: (updated) => handlePromptChange(newNodeId, updated) }
            : { onChange: (updated) => handleNodeChange(newNodeId, updated) }),
          additionalInstructions: parentNode.data.additionalInstructions || "",
          classificationLocks: {},
          isLoading: false,
          readOnly: isPromptNode ? true : false,
          isInput: isNewTextNode || isNewImageNode,
          isPrompt: isPromptNode,
          ...createCallbacks(newNodeId, nodeType),
        };

        setNodes((nds) =>
          nds.map((n) =>
            n.id === newNodeId
              ? { ...n, data: { ...n.data, ...updatedData } }
              : n,
          ),
        );

        // Pre-fetch suggestions for this node now that it has content
        prefetchSuggestions(
          { id: newNodeId, type: nodeType, data: updatedData },
          payloadData?.context || [],
        );

        if (autoClassify) {
          applyTagToNode(
            { ...stubNode, data: { ...stubNode.data, ...updatedData } },
            "classify",
          );
        }
        if (autoSentiment) {
          applyTagToNode(
            { ...stubNode, data: { ...stubNode.data, ...updatedData } },
            "sentiment",
          );
        }

      } catch (error) {
        console.error("Error creating node:", error);
        setContextMenu(null);
      }
    },
    [
      nodes,
      selectedNode,
      setNodes,
      setEdges,
      nodeChildCount,
      reactFlowInstance,
      handlePromptChange,
      autoClassify,
      autoSentiment,
      applyTagToNode,
      applyTreeLayout,
      contextMenu,
      taskDescription,
      activeRole,
      getParentNodes,
      prefetchSuggestions,
    ],
  );

  // Stable ref so node data's onRun closure always calls the latest handleToolRun
  const handleToolRunRef = useRef(null);

  // Tool node Run: collect upstream data → POST canvas-execute → drop result node
  const handleToolRun = useCallback(
    async (toolNodeId) => {
      // Find the tool node
      const toolNode = nodes.find((n) => n.id === toolNodeId);
      if (!toolNode) return;
      const slug = toolNode.data.slug;
      if (!slug) return;

      // Mark as running
      setNodes((nds) =>
        nds.map((n) =>
          n.id === toolNodeId
            ? { ...n, data: { ...n.data, runStatus: 'running', runError: undefined } }
            : n,
        ),
      );

      // Collect upstream params from parent nodes (up to 3 levels)
      const parentNodes = getParentNodes(toolNodeId, 3);
      const rawParams = {};
      for (const parent of parentNodes) {
        if (parent.type === 'fileNode') {
          const pNode = nodes.find((n) => n.id === parent.id);
          const fileId = pNode?.data?.file_id;
          const mime = pNode?.data?.mime_type || '';
          if (fileId) {
            try {
              const text = await fetchFileText(fileId, mime);
              if (text) rawParams.file_text = text;
            } catch (_) {}
            rawParams.file_id = fileId;
            rawParams.filename = pNode?.data?.filename || fileId;
            rawParams.mime_type = mime;
          }
        } else if (parent.type === 'imageNode') {
          const pNode = nodes.find((n) => n.id === parent.id);
          if (pNode?.data?.imageUrl) rawParams.image_url = pNode.data.imageUrl;
        } else if (parent.content) {
          rawParams.input_text = (rawParams.input_text ? rawParams.input_text + '\n\n' : '') + parent.content;
        }
      }

      // Remap generic collector keys to whatever the tool's input schema expects.
      // e.g. pdf-generation wants `markdown_content`, not `file_text`.
      const schemaProps = Object.keys(toolNode.data.input_schema?.properties || {});
      const GENERIC_TEXT_KEYS = ['file_text', 'input_text'];
      const GENERIC_FILE_KEYS = ['file_id'];
      const params = { ...rawParams };
      for (const genericKey of GENERIC_TEXT_KEYS) {
        if (genericKey in rawParams && !schemaProps.includes(genericKey)) {
          // Find first schema string property that isn't already set
          const target = schemaProps.find(
            (k) => !GENERIC_TEXT_KEYS.includes(k) && !GENERIC_FILE_KEYS.includes(k) && !(k in params)
          );
          if (target) {
            params[target] = rawParams[genericKey];
            delete params[genericKey];
          }
        }
      }

      const resultId = generateUniqueId();

      try {
        const { data } = await axios.post(`/api/tools/${slug}/canvas-execute`, { params });

        // Mark done
        setNodes((nds) =>
          nds.map((n) =>
            n.id === toolNodeId
              ? { ...n, data: { ...n.data, runStatus: 'done' } }
              : n,
          ),
        );

        // Tools return their payload directly (not wrapped in data.result)
        const payload = data.result ?? data;
        const fileUrl = payload.public_url || payload.download_url || payload.url || null;
        const isUrl = typeof fileUrl === 'string';
        const resultContent = isUrl
          ? fileUrl
          : (payload.text || payload.content || (typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)));

        // Create result node below tool node
        const resultNodeType = isUrl ? 'toolOutputNode' : 'textNode';
        const resultNode = {
          id: resultId,
          type: resultNodeType,
          position: {
            x: (toolNode.position?.x || 0),
            y: (toolNode.position?.y || 0) + 220,
          },
          data: isUrl ? {
            filename: payload.filename || 'output',
            fileUrl,
            mimeType: payload.mime_type || '',
            toolName: toolNode.data.name || slug,
            onDelete: () => setNodes((n) => n.filter((x) => x.id !== resultId)),
          } : {
            title: `${toolNode.data.name || slug} output`,
            content: resultContent || '(no output)',
            isOutput: true,
            readOnly: true,
            tags: [],
            classificationLocks: {},
            handleColor: '#6366f1',
            ...createCallbacks(resultId, 'textNode'),
          },
        };

        const resultEdge = {
          id: `e${toolNodeId}-${resultId}`,
          source: toolNodeId,
          target: resultId,
          type: 'smoothstep',
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { stroke: '#6366f1' },
        };

        setNodes((nds) => [...nds, resultNode]);
        setEdges((eds) => [...eds, resultEdge]);
        setNodeChildCount((prev) => ({
          ...prev,
          [toolNodeId]: (prev[toolNodeId] || 0) + 1,
        }));
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Execution failed';
        setNodes((nds) =>
          nds.map((n) =>
            n.id === toolNodeId
              ? { ...n, data: { ...n.data, runStatus: 'error', runError: msg } }
              : n,
          ),
        );
      }
    },
    [nodes, edges, getParentNodes, setNodes, setEdges, createCallbacks],
  );
  // Keep ref current so stale onRun closures in node data always call latest version
  handleToolRunRef.current = handleToolRun;

  // Tool panel drag-and-drop onto canvas
  const handleToolDragStart = useCallback((event, tool) => {
    event.dataTransfer.setData('application/flowize-tool', JSON.stringify(tool));
    event.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleCanvasDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || !reactFlowWrapper.current) return;
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      // Tool drop
      const toolRaw = event.dataTransfer.getData('application/flowize-tool');
      if (toolRaw) {
        const tool = JSON.parse(toolRaw);
        const newId = generateUniqueId();
        setNodes((nds) => [...nds, {
          id: newId,
          type: 'toolNode',
          position,
          data: {
            ...tool,
            title: tool.name,
            onDelete: () => setNodes((n) => n.filter((x) => x.id !== newId)),
            onRun: () => handleToolRunRef.current && handleToolRunRef.current(newId),
          },
        }]);
        return;
      }

      // File drop — images render as imageNode, everything else as fileNode
      const fileRaw = event.dataTransfer.getData('application/flowize-file');
      if (fileRaw) {
        const file = JSON.parse(fileRaw);
        const newId = generateUniqueId();
        const isImage = file.mime_type?.startsWith('image/');
        const nodeType = isImage ? 'imageNode' : 'fileNode';
        const imageUrl = file.public_url || null;
        setNodes((nds) => [...nds, {
          id: newId,
          type: nodeType,
          position,
          data: {
            ...file,
            title: file.filename,
            ...(imageUrl && { imageUrl }),
            isInput: true,
            readOnly: false,
            onDelete: () => setNodes((n) => n.filter((x) => x.id !== newId)),
            ...createCallbacks(newId, nodeType),
          },
        }]);

        // Background: extract schema preview for non-image files and cache in node data
        if (!isImage && file.file_id) {
          fetchFileText(file.file_id, file.mime_type || '', 8 * 1024).then((rawText) => {
            if (!rawText) return;
            const schema = extractFileSchema(rawText, file.mime_type || '');
            if (!schema) return;
            setNodes((nds) => nds.map((n) =>
              n.id === newId ? { ...n, data: { ...n.data, schema_preview: schema } } : n
            ));
          }).catch(() => {});
        }
        return;
      }
    },
    [reactFlowInstance, setNodes, createCallbacks],
  );

  const handleCanvasDragOver = useCallback((event) => {
    const types = event.dataTransfer.types;
    if (types.includes('application/flowize-tool') || types.includes('application/flowize-file')) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  // Chat bar submit:
  //   1. Creates a visible PromptNode (violet, read-only) as child of the selected node
  //   2. Immediately creates a "thinking" loading node as child of the prompt node
  //   3. Fires AI with (prompt + parent context) and replaces the thinking node with result
  const handleChatSubmit = useCallback(
    async ({ text, targetNode }) => {
      if (targetNode) {
        const parentNode = nodes.find((n) => n.id === targetNode.id);
        if (!parentNode) return;

        const promptColor = getOperationColor('create-prompt');
        const promptId = generateUniqueId();
        const resultId = generateUniqueId();

        // ── 1. Prompt node ────────────────────────────────────────────────
        const promptNode = {
          id: promptId,
          type: 'promptNode',
          position: { x: 0, y: 0 },
          data: {
            title: text.length <= 40 ? text : 'Prompt',
            content: text,
            isPrompt: true,
            readOnly: true,
            isInput: false,
            isOutput: false,
            isLoading: false,
            tags: [],
            classificationLocks: {},
            handleColor: promptColor,
            ...createCallbacks(promptId, 'promptNode'),
          },
        };
        const promptEdge = {
          id: `e${parentNode.id}-${promptId}`,
          source: parentNode.id,
          target: promptId,
          type: 'smoothstep',
          markerEnd: { type: MarkerType.ArrowClosed },
          label: 'Prompt',
          style: { stroke: promptColor },
          labelStyle: { fill: promptColor, fontSize: 10 },
          hidden: parentNode.id === 'root',
        };

        // ── 2. Thinking / loading node ────────────────────────────────────
        const thinkingNode = {
          id: resultId,
          type: 'textNode',
          position: { x: 0, y: 0 },
          data: {
            title: '…',
            content: '',
            isLoading: true,
            readOnly: true,
            isOutput: true,
            tags: [],
            classificationLocks: {},
            handleColor: promptColor,
            ...createCallbacks(resultId, 'textNode'),
          },
        };
        const resultEdge = {
          id: `e${promptId}-${resultId}`,
          source: promptId,
          target: resultId,
          type: 'smoothstep',
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { stroke: promptColor },
        };

        // Add both nodes + edges, then layout
        setContextMenu(null);
        requestAnimationFrame(() => {
          setNodes((nds) => [...nds, promptNode, thinkingNode]);
          setNodeChildCount((prev) => ({
            ...prev,
            [parentNode.id]: (prev[parentNode.id] || 0) + 1,
            [promptId]: 1,
          }));
          requestAnimationFrame(() => {
            setEdges((eds) => {
              const next = [...eds, promptEdge, resultEdge];
              setTimeout(() => {
                setNodes((cur) => {
                  applyTreeLayout(cur, next, { newNodeId: resultId, rfInstance: reactFlowInstance });
                  return cur;
                });
              }, 0);
              return next;
            });
          });
        });

        // ── 3. Fire AI (modality predicted from prompt + parent type) ────────
        try {
          let parentContent = parentNode.data.content || '';

          if (parentNode.type === 'fileNode') {
            // Fetch file text content so AI can actually analyse it
            const fileId = parentNode.data.file_id;
            const mime = parentNode.data.mime_type || '';
            if (fileId) {
              const fileText = await fetchFileText(fileId, mime);
              if (fileText) {
                parentContent = `File: ${parentNode.data.filename || fileId}\n\n${fileText}`;
              } else {
                parentContent = `File: ${parentNode.data.filename || fileId} (${mime}, binary — cannot read as text)`;
              }
            }
          } else if (parentNode.type === 'imageNode') {
            // For image parents, walk up to find the originating prompt text so
            // the backend can build a coherent enriched prompt (image models can't
            // consume a URL as "additional" context — they need descriptive text).
            let cur = parentNode.id;
            for (let i = 0; i < 5; i++) {
              const edge = edges.find((e) => e.target === cur);
              if (!edge) break;
              const ancestor = nodes.find((n) => n.id === edge.source);
              if (!ancestor) break;
              if (ancestor.type === 'promptNode' && ancestor.data.content) {
                parentContent = ancestor.data.content;
                break;
              }
              if (ancestor.data.content) parentContent = ancestor.data.content;
              cur = ancestor.id;
            }
          }
          const parentContext = getParentNodes(parentNode.id);

          const modality = predictOutputModality(text, parentNode.type);
          let response;
          if (modality === 'image') {
            response = await generateImageFromText({
              content: text,
              additional: parentContent,
              role: activeRole,
              task: taskDescription,
              context: parentContext,
            });
          } else {
            response = await expandText({
              content: text,
              additional: parentContent,
              role: activeRole,
              task: taskDescription,
              context: parentContext,
            });
          }

          const resultContent = extractContentFromResponse('expand', response);
          const isImage = !!(response.imageUrl || response.image_url);

          setNodes((nds) => nds.map((n) => n.id === resultId ? {
            ...n,
            type: isImage ? 'imageNode' : 'textNode',
            data: {
              ...n.data,
              title: `Response`,
              content: isImage ? '' : resultContent,
              imageUrl: response.imageUrl || response.image_url || '',
              isLoading: false,
              readOnly: true,
              isOutput: true,
            },
          } : n));

          prefetchSuggestions({ id: resultId, type: isImage ? 'imageNode' : 'textNode', data: { content: resultContent } }, parentContext);
        } catch (err) {
          console.error('Chat prompt execution failed:', err);
          setNodes((nds) => nds.map((n) => n.id === resultId ? {
            ...n,
            data: { ...n.data, title: 'Error', content: 'Failed to process prompt.', isLoading: false },
          } : n));
        }

        setSelectedNode(null);
      } else {
        // Empty canvas — create prompt chip rooted at hidden root, then fire AI
        const promptColor = getOperationColor('create-prompt');
        const promptId = generateUniqueId();
        const resultId = generateUniqueId();

        const promptNode = {
          id: promptId,
          type: 'promptNode',
          position: { x: 0, y: 0 },
          data: {
            title: text.length <= 40 ? text : 'Prompt',
            content: text,
            isPrompt: true,
            readOnly: true,
            isInput: false,
            isOutput: false,
            isLoading: false,
            tags: [],
            classificationLocks: {},
            handleColor: promptColor,
            ...createCallbacks(promptId, 'promptNode'),
          },
        };
        const thinkingNode = {
          id: resultId,
          type: 'textNode',
          position: { x: 0, y: 0 },
          data: {
            title: '…',
            content: '',
            isLoading: true,
            readOnly: true,
            isOutput: true,
            tags: [],
            classificationLocks: {},
            handleColor: promptColor,
            ...createCallbacks(resultId, 'textNode'),
          },
        };
        const rootToPrompt = {
          id: `root-${promptId}`,
          source: 'root',
          target: promptId,
          hidden: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        };
        const promptToResult = {
          id: `e${promptId}-${resultId}`,
          source: promptId,
          target: resultId,
          type: 'smoothstep',
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { stroke: promptColor },
        };

        setNodes((nds) => [...nds, promptNode, thinkingNode]);
        setNodeChildCount((prev) => ({ ...prev, [promptId]: 1 }));
        setEdges((eds) => {
          const next = [...eds, rootToPrompt, promptToResult];
          setTimeout(() => {
            setNodes((cur) => {
              applyTreeLayout(cur, next, { newNodeId: resultId, rfInstance: reactFlowInstance });
              return cur;
            });
          }, 0);
          return next;
        });

        // Fire AI with just the prompt text (no parent content)
        try {
          const modality = predictOutputModality(text, null);
          let response;
          if (modality === 'image') {
            response = await generateImageFromText({
              content: text,
              additional: '',
              role: activeRole,
              task: taskDescription,
              context: [],
            });
          } else {
            response = await expandText({
              content: text,
              additional: '',
              role: activeRole,
              task: taskDescription,
              context: [],
            });
          }
          const resultContent = extractContentFromResponse('expand', response);
          const isImage = !!(response.imageUrl || response.image_url);
          setNodes((nds) => nds.map((n) => n.id === resultId ? {
            ...n,
            type: isImage ? 'imageNode' : 'textNode',
            data: {
              ...n.data,
              title: 'Response',
              content: isImage ? '' : resultContent,
              imageUrl: response.imageUrl || response.image_url || '',
              isLoading: false,
              readOnly: true,
              isOutput: true,
            },
          } : n));
        } catch (err) {
          console.error('Root prompt execution failed:', err);
          setNodes((nds) => nds.map((n) => n.id === resultId ? {
            ...n,
            data: { ...n.data, title: 'Error', content: 'Failed to process prompt.', isLoading: false },
          } : n));
        }
      }
    },
    [nodes, createCallbacks, setNodes, setEdges, setSelectedNode, setNodeChildCount,
     applyTreeLayout, reactFlowInstance, getParentNodes, activeRole, taskDescription, prefetchSuggestions],
  );

  // Save the current graph as a human-authored Flow on the server
  const handleSaveGraph = useCallback(async () => {
    if (!reactFlowInstance) return null;
    const flow = reactFlowInstance.toObject();
    try {
      const result = await saveFlow({
        id: flowId || undefined,
        graph: flow,
        name: graphName,
        role: activeRole || undefined,
        humanAuthored: true,
        autoSaved: false,
      });
      if (!flowId) setFlowId(result.id);
      return flow;
    } catch (err) {
      console.error('Failed to save flow:', err);
      return flow;
    }
  }, [reactFlowInstance, flowId, graphName, activeRole]);

  // Debounced auto-save (3 s) — fires on any node/edge change once a node exists
  const triggerAutoSave = useCallback(() => {
    if (!reactFlowInstance) return;
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const flow = reactFlowInstance.toObject();
      const realNodes = flow.nodes?.filter((n) => n.id !== 'root' && !n.hidden);
      if (!realNodes?.length) return;
      try {
        const result = await saveFlow({
          id: flowId || undefined,
          graph: flow,
          name: graphName,
          role: activeRole || undefined,
          humanAuthored: true,
          autoSaved: true,
        });
        if (!flowId) setFlowId(result.id);
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    }, 3000);
  }, [reactFlowInstance, flowId, graphName, activeRole]);

  // Open the flow picker popup
  const handleLoadGraph = useCallback(() => {
    setShowFlowPicker(true);
  }, []);


  // Reload the graph (reset to initial state)
  const handleReloadGraph = useCallback(() => {
    setNodes(
      initialNodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          ...createCallbacks(n.id, n.type),
        },
      })),
    );
    setEdges(initialEdges);
    setGraphName(randomWords({ exactly: 3, join: "-" })); // Generate a new random name
    setNodeChildCount({});
  }, [setNodes, setEdges]);

  // Start a completely new graph and clear current id
  const handleNewGraph = useCallback(() => {
    // Show task selection modal before creating new graph
    setShowTaskSelectionModal(true);
  }, []);

  // Handle task selection and create new graph
  const handleTaskSelected = useCallback((taskData) => {
    // Reset the graph
    handleReloadGraph();
    setGraphId(null);
    setFlowId(null);

    // Set task description if provided
    if (taskData && taskData.description) {
      setTaskDescription(taskData.description);
    } else {
      setTaskDescription("");
    }
  }, [handleReloadGraph]);

  // Download the graph as JSON
  const handleDownloadGraph = useCallback(() => {
    if (reactFlowInstance) {
      const flow = reactFlowInstance.toObject();
      const dataStr = JSON.stringify(flow, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

      const exportFileName = `${graphName}.json`; // Use the graph name as the filename

      const linkElement = document.createElement("a");
      console.log("Downloading graph data:", exportFileName, dataStr); // Debugging log
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", exportFileName);
      linkElement.click();
    }
  }, [reactFlowInstance, graphName]);

  // Handle graph upload
  const handleUploadGraph = useCallback(
    (uploadedGraph) => {
      if (uploadedGraph) {
        const mappedNodes = (uploadedGraph.nodes || []).map((n) => ({
          ...n,
          data: {
            ...n.data,
            ...createCallbacks(n.id, n.type),
          },
        }));
        setNodes(mappedNodes);
        setEdges(uploadedGraph.edges || []);

        // Rebuild child count map
        const childCountMap = {};
        uploadedGraph.edges.forEach((edge) => {
          if (!childCountMap[edge.source]) {
            childCountMap[edge.source] = 0;
          }
          childCountMap[edge.source]++;
        });
        setNodeChildCount(childCountMap);

        alert("Graph uploaded successfully!");
      } else {
        console.error("Uploaded graph data is invalid.");
      }
    },
    [setNodes, setEdges],
  );

  const handleRoleChange = useCallback(
    (role) => {
      if (role && typeof role === "object") {
        setActiveRole(role.role);
        setActiveRoleLabel(role.label || role.role);
      } else {
        setActiveRole(role || "");
        setActiveRoleLabel(role || "");
      }
    },
    [setActiveRole, setActiveRoleLabel],
  );

  // Load user profile on mount to get their saved role
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // axios (not fetch) so the in-memory Bearer default is sent
        const response = await axios.get('/user/profile', { validateStatus: () => true });
        if (response.status >= 200 && response.status < 300) {
          const profile = response.data;
          if (profile.role) {
            // Try to fetch all roles to get the proper label
            try {
              const rolesResponse = await axios.get('/api/roles', { validateStatus: () => true });
              if (rolesResponse.status >= 200 && rolesResponse.status < 300) {
                const rolesData = rolesResponse.data;
                const matchingRole = rolesData.roles?.find(r => r.role === profile.role);
                if (matchingRole) {
                  handleRoleChange({ role: matchingRole.role, label: matchingRole.label });
                } else {
                  // Role not found in list, use role value as label
                  handleRoleChange({ role: profile.role, label: profile.role });
                }
              } else {
                // Couldn't fetch roles, use role value as label
                handleRoleChange({ role: profile.role, label: profile.role });
              }
            } catch (err) {
              console.error('Failed to fetch roles for label:', err);
              // Use role value as label if roles fetch fails
              handleRoleChange({ role: profile.role, label: profile.role });
            }
          }
        }
      } catch (err) {
        console.error('Failed to load user profile:', err);
      }
    };
    loadUserProfile();
  }, [handleRoleChange]);

  const loadGraphObject = useCallback(
    (graph) => {
      if (!graph) return;
      const mappedNodes = (graph.nodes || []).map((n) => ({
        ...n,
        data: {
          ...n.data,
          ...createCallbacks(n.id, n.type),
        },
      }));
      setNodes(mappedNodes);
      setEdges(graph.edges || []);
      const childCountMap = {};
      (graph.edges || []).forEach((edge) => {
        childCountMap[edge.source] = (childCountMap[edge.source] || 0) + 1;
      });
      setNodeChildCount(childCountMap);
      setGraphName(graph.name || randomWords({ exactly: 3, join: "-" }));
      setGraphId(graph.id || null);
      handleRoleChange(graph.role || "");
    },
    [setNodes, setEdges, handleNodeChange],
  );

  // Load a flow from the server into the canvas (defined after handleRoleChange + loadGraphObject)
  const handleFlowSelected = useCallback(
    async (flowItem) => {
      setShowFlowPicker(false);
      try {
        const flow = await getFlow(flowItem.id);
        loadGraphObject({ ...(flow.graph || {}), name: flow.name, id: flow.id, role: flow.role });
        setFlowId(flow.id);
      } catch (err) {
        console.error('Failed to load flow:', err);
      }
    },
    [loadGraphObject],
  );

  useCanvasDeepLink({
    onNew: handleNewGraph,
    onOpenFlow: (id) => handleFlowSelected({ id }),
  });

  useEffect(() => {
    if (!activeRole) return;
    const id = graphId || Date.now();
    if (!graphId) setGraphId(id);
    saveGraph({ id, role: activeRole, name: graphName, nodes, edges });
    triggerAutoSave();
  }, [nodes, edges, activeRole, graphName]);

  const { logout } = useAuth(); // ✅ Correct: top-level in component
  const navigate = useNavigate(); // ✅ Correct: top-level in component

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="app-container">
      <Toolbar
        onSave={handleSaveGraph}
        onLoad={handleLoadGraph}
        onReload={handleReloadGraph}
        onDownload={handleDownloadGraph}
        onUpload={handleUploadGraph}
        onNewGraph={handleNewGraph}
        autoClassify={autoClassify}
        setAutoClassify={setAutoClassify}
        autoSentiment={autoSentiment}
        setAutoSentiment={setAutoSentiment}
        taskDescription={taskDescription}
        setTaskDescription={setTaskDescription}
        roleLabel={activeRoleLabel}
        onRoleChange={handleRoleChange}
      />
      <div className="main-content">
        <ToolPanel
          onDragStart={handleToolDragStart}
          isOpen={toolPanelOpen}
          onToggle={() => setToolPanelOpen((p) => !p)}
          flowId={flowId}
        />
        <div
          className="reactflow-wrapper"
          ref={reactFlowWrapper}
          onDrop={handleCanvasDrop}
          onDragOver={handleCanvasDragOver}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={onInit}
            onPaneClick={onPaneClick}
            onPaneContextMenu={onPaneContextMenu}
            onNodeContextMenu={onNodeContextMenu}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.01}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.1 }}
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>

          {contextMenu && (
            <ContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              nodeType={contextMenu.nodeType}
              nodeData={contextMenu.nodeData}
              role={activeRole}
              onCreateNode={handleCreateNode}
              onCreateAction={handleOpenActionModal}
              onAnalyzeWorkflow={handleAnalyzeWorkflow}
              onUploadAttachFile={handleUploadAttachFile}
              onSelectFromLibrary={handleSelectFromLibrary}
              onClose={() => setContextMenu(null)}
              onSuggestPrefill={(prompt) => { setChatPrefill(prompt); setContextMenu(null); }}
              autoClassify={autoClassify}
              autoSentiment={autoSentiment}
              taskDescription={taskDescription}
              aiSuggestions={
                contextMenu.nodeData?.id
                  ? (suggestionCache[contextMenu.nodeData.id]?.suggestions || null)
                  : null
              }
            />
          )}
          <ActionModal
            show={showActionModal}
            onClose={() => setShowActionModal(false)}
          />
          <TaskSelectionModal
            show={showTaskSelectionModal}
            onClose={() => setShowTaskSelectionModal(false)}
            onTaskSelect={handleTaskSelected}
            userRole={activeRole}
          />
          <FilePicker
            show={showFilePicker}
            onClose={() => setShowFilePicker(false)}
            onSelect={handleFileSelected}
            multiSelect={false}
          />
          <FlowPickerModal
            show={showFlowPicker}
            onClose={() => setShowFlowPicker(false)}
            onSelect={handleFlowSelected}
          />
          <CanvasChatBar
            selectedNode={selectedNode}
            onSubmit={handleChatSubmit}
            onClearSelection={() => setSelectedNode(null)}
            prefill={chatPrefill}
            onPrefillConsumed={() => setChatPrefill('')}
          />
        </div>
      </div>
    </div>
  );
}

// LandingPage imported from ./components/LandingPage.jsx

// Backward compatibility alias
function GraphApp() {
  return <CanvasView />;
}

// Route wrapper: pulls org_id from auth user profile, passes to BillingDashboard
function BillingDashboardRoute() {
  const { user } = useAuth();
  const orgId = user?.organisation_id ? String(user.organisation_id) : null;
  const isAdmin = user?.is_admin || false;
  if (!orgId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        No organisation found. Please contact support.
      </div>
    );
  }
  return <BillingDashboard orgId={orgId} isAdmin={isAdmin} />;
}

function App() {
  return (
    <AuthProvider>
      <StepUpHost />
        <SSEProvider>
        <ActiveWorkflowsProvider>
        <Router>
          <ToastHost />
          <Routes>
            <Route path="/login" element={<EmailSignIn />} />
            <Route path="/signup" element={<EmailSignIn />} />
            <Route path="/signin/check" element={<CheckInbox />} />
            <Route path="/signin/confirm" element={<ConfirmSignIn />} />
            <Route path="/signin/expired" element={<LinkExpired />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/help" element={<HelpPage />} />
            {/* Authenticated routes share AppLayout — persistent brand-bar
                above every page. Logo click → /app is the universal home
                affordance. See AppLayout.jsx for the rationale (brainstorm-n5u8.7). */}
            <Route
              element={
                <PrivateRoute>
                  <AppLayout />
                </PrivateRoute>
              }
            >
              <Route path="/app" element={<HomePage />} />
              <Route path="/tasks" element={<TasksRoute />} />
              <Route path="/tasks/:id" element={<SavedTaskPage />} />
              <Route path="/tasks/:id/run" element={<RunTaskPage />} />
              <Route path="/tasks/:id/public" element={<PublicTaskPage />} />
              <Route path="/runs" element={<RunsRoute />} />
              <Route path="/data" element={<DataRoute />} />
              {modeRouteElements()}
              <Route path="/profile" element={<UserProfilePage />} />
              <Route path="/start" element={<FirstRunWork />} />
              <Route path="/start/ways" element={<FirstRunWays />} />
              {/* Legacy route for backward compatibility */}
              <Route path="/graph" element={<GraphApp />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/org/billing" element={<OrgBillingPage />} />
              <Route path="/billing/dashboard" element={<BillingDashboardRoute />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
            </Route>
            <Route path="/" element={<LandingPage />} />
            {/* Remove the catch-all PrivateRoute */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <FeedbackWidget />
        </Router>
        </ActiveWorkflowsProvider>
        </SSEProvider>
    </AuthProvider>
  );
}

export default App;
